import React, { useState } from 'react';
import Icon, { IconName, getAllIconNames } from './icons/Icon';
import './IconsPage.css';

// Icon.tsxから自動的にアイコン名の配列を取得
const allIconNames: IconName[] = getAllIconNames();

interface IconCardProps {
  iconName: IconName;
  size: number;
}

const IconCard: React.FC<IconCardProps> = ({ iconName, size }) => {
  const [copied, setCopied] = useState(false);

  const handleCopyName = async () => {
    try {
      await navigator.clipboard.writeText(iconName);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy icon name:', error);
    }
  };

  return (
    <div className="icon-card" onClick={handleCopyName}>
      <div className="icon-card__visual">
        <Icon name={iconName} size={size} />
      </div>
        <div className="icon-card__info">
          <div className="icon-card__name text-single-small">{iconName}</div>
          {copied && <div className="icon-card__copied text-single-small">Copied!</div>}
        </div>
    </div>
  );
};

const IconsPage: React.FC = () => {
  const [iconSize, setIconSize] = useState(24);
  const [searchQuery, setSearchQuery] = useState('');

  // 検索フィルタリング
  const filteredIcons = allIconNames.filter(iconName =>
    iconName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIconSize(Number(event.target.value));
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  return (
    <div className="icons-page">
      <div className="icons-page__header">
        <h1 className="icons-page__title text-heading-xlarge">アイコン一覧</h1>
        <p className="icons-page__description text-body-medium">
          {allIconNames.length}個のアイコンが利用可能です。アイコンをクリックして名前をコピーできます。
        </p>
        
        <div className="icons-page__controls">
          <div className="icons-page__control">
            <label htmlFor="icon-size" className="text-body-small">サイズ: {iconSize}px</label>
            <input
              id="icon-size"
              type="range"
              min="16"
              max="64"
              value={iconSize}
              onChange={handleSizeChange}
              className="icons-page__size-slider"
            />
          </div>
          
          <div className="icons-page__control">
            <label htmlFor="search" className="text-body-small">検索</label>
            <input
              id="search"
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="アイコン名で検索..."
              className="icons-page__search text-single-large"
            />
          </div>
        </div>
      </div>

      <div className="icons-page__content">
        <div className="icons-page__grid">
          {filteredIcons.map(iconName => (
            <IconCard
              key={iconName}
              iconName={iconName}
              size={iconSize}
            />
          ))}
        </div>
        
        {filteredIcons.length === 0 && searchQuery && (
          <div className="icons-page__no-results text-body-medium">
            検索条件 "{searchQuery}" に一致するアイコンが見つかりません
          </div>
        )}
      </div>

      <div className="icons-page__usage">
        <h2 className="text-heading-large">使用方法</h2>
        <pre className="icons-page__code-example text-body-small">
{`import Icon from './icons/Icon';

// 基本的な使用方法
<Icon name="chevronRight" size={24} />

// サイズ指定
<Icon name="check" size={32} />

// カスタムクラス
<Icon name="close" className="custom-class" />`}
        </pre>
      </div>
    </div>
  );
};

export default IconsPage;
